import { DEFAULT_LOCALE, messages, type Locale, type Messages } from "@shared/i18n";
import {
  contactMessage,
  dismissReasonLabel,
  LEAD_STATUSES,
  leadStatusLabel,
  leadMatchesSearch,
  normalizeNamedContacts,
  type ContactCandidate,
  type Lead,
  type LeadSignal,
  type LeadStatus,
} from "@shared/lib/leads-shared";

/**
 * Export CSV du pipeline (`GET /api/leads/export`). Module pur, sans
 * dépendance serveur : la route lui passe des leads déjà cloisonnés (compte,
 * offre, assignation) par `listLeadsForExport` (`shared/lib/leads.ts`), et
 * l'écran y lit la même sélection pour savoir combien de leads partiront.
 *
 * Le fichier s'ouvre tel quel dans Excel en français comme dans Google Sheets :
 * UTF-8 avec BOM (sans lui, Excel lit du Windows-1252 et casse les accents),
 * « ; » comme séparateur (la virgule est le séparateur décimal), fins de ligne
 * CRLF, guillemets RFC 4180, dates AAAA-MM-JJ. Une cellule qu'un tableur
 * prendrait pour une formule est neutralisée : le texte vient de pages web
 * publiques et de l'agent, pas de l'utilisateur.
 */

/** Les filtres de l'écran : tous, un statut, les favoris, les archivés. */
export const LEADS_EXPORT_FILTERS = ["all", ...LEAD_STATUSES, "favorites", "archived"] as const;
export type LeadsExportFilter = (typeof LEADS_EXPORT_FILTERS)[number];

/**
 * Ce que l'écran sélectionne : le filtre (un statut, ou les archivés), le
 * bouton « Favoris » et la recherche. Le raccourci « non lus » n'en fait pas
 * partie : il suit la lecture en cours, pas le pipeline.
 */
export type LeadsExportQuery = {
  filter: LeadsExportFilter;
  favoritesOnly?: boolean;
  search?: string;
};

/**
 * Plafond d'un fichier, très au-delà d'un pipeline réel : au-delà, le fichier
 * garde les leads les plus récemment vus et la réponse porte l'en-tête
 * `X-Hune-Truncated` (la limite appliquée), que le bouton fait lire.
 */
export const LEADS_EXPORT_LIMIT = 10_000;
export const LEADS_EXPORT_TRUNCATED_HEADER = "X-Hune-Truncated";

/** Longueur maximale de la recherche transmise (le champ de l'écran n'en a pas). */
const SEARCH_MAX_LENGTH = 200;

/** `null` pour un filtre inconnu ; absent, c'est « tous ». */
export function parseLeadsExportFilter(value: string | null | undefined): LeadsExportFilter | null {
  if (!value) return "all";
  return (LEADS_EXPORT_FILTERS as readonly string[]).includes(value) ? (value as LeadsExportFilter) : null;
}

/** La sélection demandée par l'adresse (`filter`, `favorites`, `q`) ; `null` pour un filtre inconnu. */
export function parseLeadsExportQuery(params: URLSearchParams): Required<LeadsExportQuery> | null {
  const filter = parseLeadsExportFilter(params.get("filter"));
  if (!filter) return null;
  return {
    filter,
    favoritesOnly: ["1", "true"].includes(params.get("favorites") ?? ""),
    search: (params.get("q") ?? "").trim().slice(0, SEARCH_MAX_LENGTH),
  };
}

/**
 * La sélection en critères simples, que la requête SQL de l'export et le
 * compte de l'écran appliquent à l'identique : les archivés à part (tous
 * statuts confondus), sinon les leads actifs du statut choisi ; les favoris se
 * combinent aux deux, la recherche aussi.
 */
export function leadsExportCriteria({ filter, favoritesOnly = false, search = "" }: LeadsExportQuery) {
  return {
    archived: filter === "archived",
    status: (LEAD_STATUSES as readonly string[]).includes(filter) ? (filter as LeadStatus) : null,
    favorites: favoritesOnly || filter === "favorites",
    search: search.trim(),
  };
}

/** Même sélection que l'écran, sur des leads déjà chargés (le compte du bouton). */
export function filterLeadsForExport<
  T extends Pick<Lead, "archived_at" | "favorite" | "status" | "name" | "category"> & {
    latest_signal: { market_clue: string } | null;
  },
>(leads: readonly T[], query: LeadsExportQuery): T[] {
  const criteria = leadsExportCriteria(query);
  return leads.filter((lead) => {
    if (criteria.archived ? !lead.archived_at : lead.archived_at) return false;
    if (criteria.favorites && !lead.favorite) return false;
    if (criteria.status && lead.status !== criteria.status) return false;
    return leadMatchesSearch(lead, criteria.search);
  });
}

/** Adresse de téléchargement pour la sélection de l'écran (valeurs par défaut omises). */
export function leadsExportHref({ filter, favoritesOnly = false, search = "" }: LeadsExportQuery) {
  const params = new URLSearchParams();
  if (filter !== "all") params.set("filter", filter);
  if (favoritesOnly && filter !== "favorites") params.set("favorites", "1");
  if (search.trim()) params.set("q", search.trim());
  const query = params.toString();
  return `/api/leads/export${query ? `?${query}` : ""}`;
}

/** Ce que l'export lit d'un interlocuteur (pas tout son portrait). */
export type LeadExportContact = Pick<
  ContactCandidate,
  | "priority_rank"
  | "fit_score"
  | "contact_name"
  | "contact_role"
  | "contact_linkedin_url"
  | "public_professional_email"
  | "icebreakers"
  | "outreach_email"
>;

/** Ce que l'export lit du dernier signal. */
export type LeadExportSignal = Pick<
  LeadSignal,
  "market_clue" | "priority" | "observed_on" | "source_link" | "message_draft" | "created_at"
>;

/** Un lead tel que l'export le lit ; un `Lead` complet de l'écran en est un. */
export type LeadExportRecord = Pick<
  Lead,
  | "id"
  | "name"
  | "category"
  | "website"
  | "status"
  | "favorite"
  | "archived_at"
  | "dismiss_reason"
  | "dismiss_note"
  | "osint_score"
  | "contact_name"
  | "contact_role"
  | "contact_linkedin_url"
  | "first_seen_at"
  | "last_seen_at"
  | "reactivated_at"
> & {
  signal_count: number;
  latest_signal: LeadExportSignal | null;
  contacts: LeadExportContact[];
};

type Cell = string | number | null | undefined;

/**
 * Column separator, the one a spreadsheet in that language opens without an
 * import step: `;` for French Excel (where `,` is the decimal mark), `,` for
 * English.
 */
const SEPARATORS: Record<Locale, string> = { fr: ";", en: "," };
const EOL = "\r\n";
const BOM = "﻿";

/** Jour calendaire à Paris : un lead repéré à 23 h 30 UTC l'a été le lendemain. */
const parisDayParts = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Paris",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function parisDay(time: number) {
  const parts = Object.fromEntries(parisDayParts.formatToParts(new Date(time)).map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

const TIMESTAMP = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:/;
/** Un jour (ou un mois seul) en tête, que ne prolonge ni un chiffre ni un jour tronqué. */
const LEADING_DAY = /^(\d{4}-(?:0[1-9]|1[0-2])(?:-(?:0[1-9]|[12]\d|3[01]))?)(?!\d|-\d)/;

/**
 * Une date en AAAA-MM-JJ : un horodatage ramené au jour de Paris, un jour tel
 * quel même suivi d'une précision (« 2026-10-12 (annonce) »), un fait daté au
 * mois en AAAA-MM. Vide pour ce qui n'est pas une date (« printemps 2026 »,
 * « Date non disponible »).
 */
export function csvDate(value: string | null | undefined): string {
  const text = value?.trim();
  if (!text) return "";
  if (TIMESTAMP.test(text)) {
    const time = Date.parse(text);
    return Number.isNaN(time) ? text.slice(0, 10) : parisDay(time);
  }
  return LEADING_DAY.exec(text)?.[1] ?? "";
}

/**
 * Une cellule CSV. Un texte qui commence par = + - @, une tabulation ou un
 * retour chariot prend une apostrophe devant (sinon Excel et Sheets l'évaluent
 * comme une formule) ; un texte qui contient le séparateur, une virgule, un
 * guillemet ou un saut de ligne passe entre guillemets, les guillemets
 * doublés — la virgule protégée, un tableur qui devine le séparateur ne la
 * prend pas pour lui.
 */
export function csvCell(value: Cell): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  const text = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[;,"\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** Le fichier : BOM, puis une ligne CRLF par enregistrement. */
export function toCsv(rows: ReadonlyArray<ReadonlyArray<Cell>>, locale: Locale = DEFAULT_LOCALE): string {
  return BOM + rows.map((row) => row.map(csvCell).join(SEPARATORS[locale]) + EOL).join("");
}

/** Columns of the file, in order; their headers come from the dictionary (`leads.export.headers`). */
const LEADS_CSV_COLUMNS = [
  "organization",
  "website",
  "status",
  "score",
  "latestSignal",
  "signalDate",
  "signalSource",
  "signalCount",
  "firstSeen",
  "dismissReason",
  "contact",
  "role",
  "email",
  "linkedin",
  "subject",
  "message",
  "followUp",
  "link",
] as const satisfies ReadonlyArray<keyof Messages["leads"]["export"]["headers"]>;

/** Header row in a language: same columns, same order, whatever the language. */
export function leadsCsvHeaders(locale: Locale = DEFAULT_LOCALE): string[] {
  const headers = messages(locale).leads.export.headers;
  return LEADS_CSV_COLUMNS.map((column) => headers[column]);
}

/** French header row (the default file). */
export const LEADS_CSV_HEADERS: readonly string[] = leadsCsvHeaders(DEFAULT_LOCALE);

/**
 * Une ligne par lead. L'interlocuteur est le premier du classement (à défaut,
 * l'ancien contact unique de l'organisation) ; le message est celui que la
 * fiche copie (l'e-mail rédigé pour lui, sinon son accroche et le brouillon du
 * signal). Le score est celui de l'écran : la priorité du dernier signal,
 * sinon celle du dossier. La raison d'écartement ne vaut que pour un lead
 * écarté : un lead réactivé la garde en base, mais il est de nouveau en jeu.
 */
export function leadCsvRow(lead: LeadExportRecord, appUrl: string, locale: Locale = DEFAULT_LOCALE): Cell[] {
  const signal = lead.latest_signal;
  const contact = normalizeNamedContacts(lead.contacts ?? [])[0] ?? null;
  const email = contact?.outreach_email ?? null;
  const score = signal?.priority ?? lead.osint_score;
  const dismissal =
    lead.status === "dismissed" && lead.dismiss_reason
      ? [dismissReasonLabel(lead.dismiss_reason, locale), lead.dismiss_note].filter(Boolean).join(" — ")
      : null;
  return [
    lead.name,
    lead.website,
    leadStatusLabel(lead.status, locale),
    typeof score === "number" ? Math.round(score) : null,
    signal?.market_clue,
    // La date du fait quand l'agent l'a datée, sinon celle où Hune l'a relevé.
    signal ? csvDate(signal.observed_on) || csvDate(signal.created_at) : null,
    signal?.source_link,
    lead.signal_count,
    csvDate(lead.first_seen_at),
    dismissal,
    contact ? contact.contact_name : lead.contact_name,
    contact ? contact.contact_role : lead.contact_role,
    contact?.public_professional_email,
    contact ? contact.contact_linkedin_url : lead.contact_linkedin_url,
    email?.subject,
    contactMessage(signal?.message_draft, contact),
    email?.follow_up,
    `${appUrl}/app/leads?lead=${lead.id}`,
  ];
}

export function buildLeadsCsv(
  leads: readonly LeadExportRecord[],
  { appUrl, locale = DEFAULT_LOCALE }: { appUrl: string; locale?: Locale },
): string {
  return toCsv([leadsCsvHeaders(locale), ...leads.map((lead) => leadCsvRow(lead, appUrl, locale))], locale);
}

/** `hune-leads-<offre>-<AAAA-MM-JJ>.csv`, le jour pris à Paris. */
export function leadsExportFilename(scopeName: string | null, now: Date = new Date()) {
  const slug = (scopeName ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .toLowerCase()
    .slice(0, 60)
    .replace(/^-+|-+$/g, "");
  return `${["hune-leads", slug, parisDay(now.getTime())].filter(Boolean).join("-")}.csv`;
}
